<template>
  <phone-preview>
    <div class="text-center py-5 bg-theme-background">
      {{ senderIdLocal }}
    </div>
    <div
      v-show="messagePreviewHtml.length > 0
          || messageType === $enums.Campaign.MessageType.MARKETING"
      v-html="messagePreviewHtml"
      class="message-preview">
    </div>

    <div
      style="position: absolute; bottom: 0; border-top: 1px solid #dae1e7"
      class="flex justify-between items-center py-2 px-2">
      <div>
        <vue-tel-input
          v-model="testPhoneNumber"
          :class="{ 'border-danger': invalidPhoneNumber}"
          :disabledFetchingCountry="true"
          :dynamicPlaceholder="false"
          :enabledCountryCode="true"
          :validCharactersOnly="true"
          :preferredCountries="['AU', 'CO']"
          :defaultCountry="'AU'"
          :placeholder="$tc('$General.PhoneNumber', 1)"
          @validate="(payload) => (this.testPhoneNumberValidate = payload)">
        </vue-tel-input>
      </div>

      <vs-button
        size="small"
        class="ml-2"
        :disabled="invalidPhoneNumber"
        @click="$emit('test', testPhoneNumberValidate.number.international)">
        {{ $t('$General.Test') }}
      </vs-button>
    </div>
  </phone-preview>
</template>

<script>
import moment from 'moment';
import { VueTelInput } from 'vue-tel-input';
import { mapActions, mapGetters } from 'vuex';

import PhonePreview from '@/views/modules/_components/PhonePreview.vue';
import enums from '@/enums';

/**
 * Component to view the preview of SMS
 *
 * @module views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEditMessagePreview
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'SMSCampaignListCreateOrEditMessagePreview',
  components: {
    VueTelInput,
    PhonePreview,
  },
  props: {
    message: {
      type: String,
      required: true,
    },
    messageType: {
      type: String,
      required: false,
      validator(type) {
        if (type === '') return true;
        return Object.values(enums.Campaign.MessageType).includes(type);
      },
    },
    interpolations: {
      type: Array,
      required: false,
      default() {
        return [];
      },
      validator(interpolations) {
        return interpolations.every((interpolation) => typeof interpolation === 'object'
          && interpolation !== null
          && 'shorthand' in interpolation
          && typeof interpolation.shorthand === 'string'
          && 'attribute' in interpolation
          && typeof interpolation.attribute === 'string');
      },
    },
    attributesFromContacts: {
      type: Array,
      required: true,
    },
    callerIdType: {
      type: String,
      required: true,
      validator(type) {
        return Object.values(enums.Sender.Caller.Type).includes(type);
      },
    },
    senderId: {
      type: [Object, String],
      required: true,
    },
    senderBusinessName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      contactToInterpolate: null,
      optOutText: '\nOpt-out',
      testPhoneNumber: '',
      testPhoneNumberValidate: null,
    };
  },
  computed: {
    ...mapGetters({
      shortReviewLink: 'auth/tenantShortReviewLink',
      tenantAccountDetails: 'auth/tenantAccountDetails',
    }),
    messageLocalInterpolated() {
      let messageInterpolated = this.message;

      this.interpolations.forEach((interpolation) => {
        if (this.contactToInterpolate && this.contactToInterpolate[interpolation.attribute]
          && interpolation.type !== this.$enums.Attributes.Type.BUSINESS
          && (interpolation.type !== this.$enums.Attributes.Type.CATEGORY
            || this.contactToInterpolate[interpolation.attribute].length > 0)
        ) {
          let placeholder = this.contactToInterpolate[interpolation.attribute];

          if (interpolation.type === this.$enums.Attributes.Type.CATEGORY) {
            placeholder = placeholder.map((item) => item.name).toString().replace(/,/g, ', ');
          }

          messageInterpolated = messageInterpolated.replace(
            interpolation.shorthand, placeholder,
          );
        } else {
          messageInterpolated = messageInterpolated.replace(
            interpolation.shorthand, this.getInterpolationPlaceholder(interpolation),
          );
        }
      });

      return messageInterpolated;
    },
    messagePreviewHtml() {
      const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/gi;
      const message = this.messageLocalInterpolated + this.optOutMessage;
      const urls = message.match(urlRegex) || [];
      let messageHtml = message.replace('\n', '<br>');

      urls.forEach((value) => {
        messageHtml = messageHtml.replace(value, `
          <a
            href="${value}"
            target="_blank">
            ${value.replace(/https?:\/\/(www\.)?/gi, '')}
          </a>`);
      });

      return messageHtml;
    },
    optOutMessage() {
      if (this.messageType === this.$enums.Campaign.MessageType.MARKETING) {
        switch (this.callerIdType) {
          case this.$enums.Sender.Caller.Type.SHARED_NUMBER:
          case this.$enums.Sender.Caller.Type.DEDICATED_NUMBER:
            return `${this.optOutText} reply STOP`;

          default:
            return `${this.optOutText} ${this.optOutUrl}`;
        }
      }

      return '';
    },
    optOutUrl() {
      return this.contactToInterpolate && this.contactToInterpolate.optOutUrl
        ? this.contactToInterpolate.optOutUrl : 'https://g2.is/XXXX';
    },
    invalidPhoneNumber() {
      return this.testPhoneNumber
        && this.testPhoneNumberValidate
        && !this.testPhoneNumberValidate.isValid;
    },
    senderIdLocal() {
      switch (this.callerIdType) {
        case this.$enums.Sender.Caller.Type.SHARED_NUMBER:
          return this.$options.filters.sentencecase(this.$t('$General.SharedNumber'));
        case this.$enums.Sender.Caller.Type.PHONE_NUMBER:
          return this.senderId.phoneNational;
        case this.$enums.Sender.Caller.Type.BUSINESS_NAME:
          return this.senderBusinessName;

        default:
          return this.$options.filters.sentencecase(this.$t('$General.ReplyNumber'));
      }
    },
  },
  watch: {
    optOutMessage(val) {
      this.$emit('opt-out-message-length', val.length);
    },
  },
  created() {
    this.fetchContactToInterpolate();
  },
  methods: {
    ...mapActions({
      fetchAllContacts: 'contact/fetchAllContacts',
    }),
    getInterpolationPlaceholder(interpolation) {
      return interpolation.type === this.$enums.Attributes.Type.DEFAULT
        ? this.getInterpolationPlaceholderByDefaults(interpolation)
        : this.getInterpolationPlaceholderByType(interpolation);
    },
    getInterpolationPlaceholderByDefaults(interpolation) {
      switch (interpolation.attribute) {
        case 'name':
          return 'Jhon Doe';

        case 'firstName':
          return 'Jhon';

        case 'lastName':
          return 'Doe';

        case 'phoneSignificant':
          return '444 44 44';

        case 'email':
          return 'name@email.com';

        default:
          return interpolation.shorthand;
      }
    },
    getInterpolationPlaceholderByType(interpolation) {
      switch (interpolation.type) {
        case this.$enums.Attributes.Type.TEXT:
          return interpolation.name;

        case this.$enums.Attributes.Type.BOOL:
          return 'Yes/No';

        case this.$enums.Attributes.Type.DATE:
          return 'DD/MM/YYYY';

        case this.$enums.Attributes.Type.NUMBER:
          return ' 0000';

        case this.$enums.Attributes.Type.CATEGORY:
          // eslint-disable-next-line no-underscore-dangle,no-case-declarations
          const attr = this.attributesFromContacts.find((a) => a._id === interpolation.attribute);

          if (attr) {
            return attr.items.map((item) => item.name).toString().replace(/,/g, ', ');
          }
          return interpolation.shorthand;

        case this.$enums.Attributes.Type.SPECIAL:
          return this.getSpecialPlaceholder(interpolation);

        case this.$enums.Attributes.Type.WAITLIST:
          return this.getWaitlistPlaceholder(interpolation);

        case this.$enums.Attributes.Type.BUSINESS:
          return this.getBusinessPlaceholder(interpolation);

        default:
          return interpolation.shorthand;
      }
    },
    getSpecialPlaceholder(interpolation) {
      switch (interpolation.attribute) {
        case this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK:
          if (this.shortReviewLink && !/^https?:\/\//gi.test(this.shortReviewLink)) {
            return `http://${this.shortReviewLink}`;
          }
          return this.shortReviewLink || interpolation.shorthand;

        default:
          return interpolation.shorthand;
      }
    },
    getWaitlistPlaceholder(interpolation) {
      switch (interpolation.attribute) {
        case this.$enums.Campaign.WaitListPlaceholders.TURN_NUMBER:
          return 3;

        case this.$enums.Campaign.WaitListPlaceholders.CURRENT_TURN_NUMBER:
          return 1;

        case this.$enums.Campaign.WaitListPlaceholders.SPOTS_AHEAD:
          return 1;

        case this.$enums.Campaign.WaitListPlaceholders.TURN_DATE:
          return moment().format('DD/MM/YYYY');

        default:
          return interpolation.shorthand;
      }
    },
    getBusinessPlaceholder(interpolation) {
      const businessDetails = this.tenantAccountDetails;

      if (!businessDetails) {
        return interpolation.shorthand;
      }

      // Access nested properties dynamically (e.g., "taxId.value")
      const value = interpolation.name === this.$enums.Campaign.BusinessPlaceholders.BUSINESS_TIMEZONE
        ? this.$t(`$TimeZones.${businessDetails[interpolation.attribute]}`)
        : interpolation.attribute.split('.').reduce((obj, key) => obj?.[key], businessDetails);

      return value || interpolation.shorthand;
    },
    async fetchContactToInterpolate() {
      const contacts = await this.fetchAllContacts({
        limit: 1,
      });

      this.contactToInterpolate = contacts && contacts.data && Array.isArray(contacts.data)
        ? contacts.data[0] : null;
    },
  },
};
</script>

<style lang="scss" scoped>
.message-preview {
  background: rgba(var(--vs-grey-light), 0.5) !important;
  margin: 20px 20px 40px 30px;
  padding: 1rem;
  border-radius: 10px;
  line-break: anywhere;
}
</style>
